import React from 'react';
import round from 'lodash/round';
import { CusEcharts } from '@/components/AsyncComponent';
import type { ECOption } from '@/components/CusEcharts';
import GeneralChart from '@/components/job-title/GeneralChart';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BarYAxisLabel from './BarYAxisLabel';
import DataInterpretationSEO from '@/components/DataInterpretationSEO';
import { DataType } from './index';
import { JobTitleContext } from '@/context/create-context';
import { SCALE_BAR_TIP } from '@/constants/g2plot';
import {
  WORK_EXPERIENCE_BENEFITS_SEO_MAP,
  WORK_EXPERIENCE_MAP,
} from '@/constants/job-title';
import { max, numberRound } from '@/utils/numbers';

const SalaryExperienceCard = () => {
  const {
    jobTitle: {
      salary_details: {
        avg_value_by_experience_details: avgValueByExperienceDetails,
      },
      city_name: cityName = '',
    },
    displayName,
  } = React.useContext(JobTitleContext);
  const barChartRef = React.useRef(null);

  const [experienceChartData, experienceBarTipPercentage] = React.useMemo<
    [DataType, Record<string, number>]
  >(() => {
    const data: DataType = [];
    const experienceBarTipPercentage: { [key: string]: number } = {};
    const experienceMax = max(
      Object.values(avgValueByExperienceDetails).map(
        (item: any) => item['max'],
      ),
    );
    Object.keys(WORK_EXPERIENCE_MAP).forEach((key) => {
      const salaryRange = Reflect.get(avgValueByExperienceDetails, key);
      if (!salaryRange) return;
      data.push({
        label: WORK_EXPERIENCE_MAP[key],
        range: [salaryRange.min, salaryRange.max],
      });
      const percentage = Number(
        (
          (SCALE_BAR_TIP * experienceMax) /
          (salaryRange.max - salaryRange.min)
        ).toFixed(4),
      );
      experienceBarTipPercentage[WORK_EXPERIENCE_MAP[key]] =
        percentage >= 1 ? 1 : percentage <= 0 ? 0 : percentage;
    });
    return [data, experienceBarTipPercentage];
  }, [avgValueByExperienceDetails]);

  const seoValue = React.useMemo(() => {
    const data: string[] = [];
    Object.keys(WORK_EXPERIENCE_MAP).forEach((key) => {
      const salaryRange = Reflect.get(avgValueByExperienceDetails, key);
      if (!salaryRange) return;
      data.push(
        `${WORK_EXPERIENCE_BENEFITS_SEO_MAP[key]}薪资区间为${round(
          salaryRange.min,
          -3,
        )}元至${round(salaryRange.max, -3)}元`,
      );
    });
    return data;
  }, [avgValueByExperienceDetails]);

  const barConfig = React.useMemo<ECOption>(() => {
    const data = [...experienceChartData];
    const reverseData = data.reverse();
    const chartLowData = reverseData.map(({ label, range }) => ({
      name: label,
      value: range[0],
    }));
    const chartHighData = reverseData.map(({ label, range }) => ({
      name: label,
      value: range[1] - range[0],
    }));

    return {
      grid: {
        show: false,
        containLabel: true,
        top: 0,
        bottom: 0,
        left: 25,
        right: 30,
      },
      xAxis: {
        type: 'value',
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: 'rgba(0,0,0,0.85)',
          formatter: (item: number) => numberRound(item),
          margin: 15,
        },
        splitLine: { show: false },
        splitNumber: 4,
      },
      yAxis: {
        interval: true,
        type: 'category',
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: { show: true, lineStyle: { width: 1, color: '#f0f0f0' } },
      },
      tooltip: {
        confine: true,
        padding: 10,
        borderColor: '#f0f0f0',
        borderWidth: 1,
        backgroundColor: '#fff',
        shadowColor: 'transparent',
        textStyle: { fontSize: 12, fontWeight: 500 },
        formatter: (params) => {
          const name = 'name' in params ? params.name : '';
          const lowValue =
            chartLowData.find((item) => item.name === name)?.value || 0;
          const highValue =
            chartHighData.find((item) => item.name === name)?.value || 0;
          return `<p>${name}</p><p style="margin-top: 4px;">平均薪资: <span style="color:#00575c">¥${numberRound(
            (lowValue * 2 + highValue) / 2,
          )}</span></p>`;
        },
      },
      series: [
        {
          type: 'bar',
          data: chartLowData,
          emphasis: { disabled: true },
          cursor: 'default',
          barWidth: 30,
          itemStyle: {
            borderWidth: 0,
            color: '#fff',
          },
          stack: 'total',
        },
        {
          type: 'bar',
          data: chartHighData,
          barWidth: 30,
          emphasis: { disabled: true },
          cursor: 'default',
          stack: 'total',
          label: {
            show: true,
            position: 'insideLeft',
            color: 'rgba(0, 0, 0, 0.85)',
            formatter: ({ dataIndex, value }) => {
              const lowData = chartLowData[dataIndex].value;
              return `${numberRound(lowData)}-${numberRound(
                lowData + Number(value),
              )}`;
            },
          },
          itemStyle: {
            borderWidth: 0,
            borderColor: 'transparent',
            shadowColor: 'transparent',
            borderRadius: 1,
            color: ({ name }) => {
              const percentage = experienceBarTipPercentage[name];
              return {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 1,
                y2: 0,
                colorStops: [
                  {
                    offset: 0,
                    color: '#008489',
                  },
                  {
                    offset: percentage,
                    color: '#008489',
                  },
                  {
                    offset: percentage,
                    color: 'rgba(8, 161, 123, 0.09)',
                  },
                  {
                    offset: 1,
                    color: 'rgba(8, 161, 123, 0.09)',
                  },
                ],
              };
            },
          },
        },
      ],
    };
  }, [experienceChartData, experienceBarTipPercentage]);

  return (
    <GeneralChart
      type="custom"
      title={`${cityName}不同年限的${displayName}收入相差多少?`}
      subTitle={
        <>
          <AccessTimeIcon className="inline-block mr-1" />
          工作年限对应的薪资区间
        </>
      }
      description={
        <DataInterpretationSEO
          lineClamp="line-clamp-none"
          className="font-normal text-opacity-85"
        >
          {`工作年限对于${`${cityName}${displayName}`}岗位的收入有多大影响？${seoValue.join(
            '，',
          )}。数据依赖于各平台的公开数据，有所波动，仅供参考。`}
        </DataInterpretationSEO>
      }
    >
      <div className="px-5 pb-6 flex tablet:px-12">
        <BarYAxisLabel
          chartData={experienceChartData}
          jobTitleName={`${cityName}${displayName}`}
        />
        <div className="w-full grow [&_canvas]:cursor-default">
          <CusEcharts chartRef={barChartRef} config={barConfig} />
        </div>
      </div>
    </GeneralChart>
  );
};

export default SalaryExperienceCard;
